/**
 * Signup identity hand-off — getting the TYPED username onto the account (#373).
 *
 * THE PROBLEM THIS EXISTS TO SOLVE
 * --------------------------------
 * The signup form collects a Username, and it was thrown away: `useAuth.signup`
 * took the argument and never used it (the `updateProfile` call was commented
 * out, under a comment claiming "the backend will use this"), so the backend saw
 * no `name` claim on the token and fell back to the email prefix — which is how
 * a user who typed "Chris" ended up named "cgrafa96".
 *
 * Threading it through is not simply "call updateProfile", because of an
 * ORDERING race that is invisible until you draw it out:
 *
 *   signup()                             onAuthStateChanged (fires on creation)
 *   ─────────────────────────────        ──────────────────────────────────────
 *   createUserWithEmailAndPassword ───▶  (a) getIdToken(true)      ~1 RTT
 *   (a) updateProfile      ~1 RTT        (b) sleep(100ms)
 *   (b) getIdToken(true)   ~1 RTT        (c) GET /auth/profile  ← CREATES the row
 *
 * The backend row is created by (c), and the name only exists in a token from
 * (b) — one round trip LATER. So without coordination the row is created with
 * the email prefix essentially every time, and the typed name is at best a
 * correction the user watches happen.
 *
 * TWO MECHANISMS, PRIMARY AND FALLBACK
 * ------------------------------------
 * 1. **The barrier** (primary). `signup` arms it before creating the account;
 *    the auth-state handler awaits it before minting its token. The row is then
 *    created from a token that already carries `name`, so it is correct on the
 *    first write — no rename, no `apply_username_change` rewrite of SOUL/USER
 *    (#184), and nothing for the user to see flicker.
 * 2. **The pending register** (fallback). The typed name is also remembered, so
 *    that if the claim did not propagate for any reason the handler can
 *    reconcile with one `PATCH /auth/profile {username}`.
 *
 * The barrier is BOUNDED (`BARRIER_TIMEOUT_MS`): a hung `updateProfile` must
 * delay a first login by a few seconds at worst, never forever — and the
 * fallback still fixes the name afterwards. It also resolves on the failure
 * path, so a rejected signup never leaves a promise nobody will settle.
 *
 * Module scope (not React state) is deliberate and follows `backgroundDelete` /
 * `backgroundTranscription`: this hand-off spans a component's own lifetime —
 * `onAuthStateChanged` fires from the SDK, and the /signup route unmounts the
 * moment the navigation happens.
 */

/** users.username is `String(50)` server-side; clip rather than reject. */
export const USERNAME_MAX_LENGTH = 50;

/** How long the auth-state handler will wait for a signup to finish naming the account. */
export const BARRIER_TIMEOUT_MS = 4000;

let barrier = null;            // { promise, settle } while a signup is in flight
let pendingUsername = null;    // the typed name, until it is reconciled

/** Trim + clip a raw form value to what the server will actually store. */
export function normalizeUsername(raw) {
    return String(raw ?? '').trim().slice(0, USERNAME_MAX_LENGTH);
}

/**
 * Open the barrier and remember the typed name. Called BEFORE the account is
 * created, so the auth-state callback that fires on creation already sees it.
 *
 * An empty name arms nothing and registers nothing: "left blank" is exactly the
 * case the server's email-prefix fallback is for.
 */
export function armSignupBarrier(username) {
    const name = normalizeUsername(username);
    pendingUsername = name || null;
    if (!name) {
        barrier = null;
        return;
    }
    let settle;
    const promise = new Promise((resolve) => { settle = resolve; });
    barrier = { promise, settle };
}

/** Close the barrier. Idempotent, and safe to call when none was armed. */
export function resolveSignupBarrier() {
    const current = barrier;
    barrier = null;
    if (current) current.settle();
}

/**
 * Wait for an in-flight signup to finish naming the account.
 *
 * Returns immediately when no signup is in flight — which is every ordinary
 * login, so this costs a normal sign-in nothing.
 */
export function awaitSignupBarrier(timeoutMs = BARRIER_TIMEOUT_MS) {
    const current = barrier;
    if (!current) return Promise.resolve();
    let timer;
    const timeout = new Promise((resolve) => {
        timer = setTimeout(resolve, timeoutMs);
    });
    return Promise.race([current.promise, timeout]).then(() => {
        clearTimeout(timer);
    });
}

/** The typed name still awaiting reconciliation, or null. */
export function pendingSignupUsername() {
    return pendingUsername;
}

/** Forget the typed name — once it is on the row, or once signup failed. */
export function clearPendingSignupUsername() {
    pendingUsername = null;
}

/** Test-only: return the module to its initial state. */
export function __resetSignupIdentity() {
    resolveSignupBarrier();
    pendingUsername = null;
}
